import { Color, IconType } from 'constants/assets'
import { FC, useEffect, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'

import Button from 'components/common/Button/Button'
import { ChargebeeHostedPageStatus } from 'constants/chargebee'
import Icon from 'components/common/Icon/Icon'
import { Path } from 'constants/router'
import { logger } from 'utils/logging'
import styles from './SubscriptionStatus.module.sass'
import { EntityKeys, queryClient } from 'utils/reactQuery'
import { useConfirmSubscription } from 'dataQueries/subscriptions.query'
import { useUserData } from 'components/contexts/UserDataContext'

enum Statuses {
  SUCCESS = 'SUCCESS',
  PROCESSING = 'PROCESSING',
  CANCELLED = 'CANCELLED',
  ERROR = 'ERROR',
}

/**
 * Workspace IDs already confirmed in this browser session.
 * Module-scoped (not a ref) on purpose: refreshing USER_ME by invalidating its query flips
 * the global `isLoading` gate in RouteSwitch, which unmounts and remounts this page. A
 * component ref would reset on remount and re-trigger confirm → userMe → remount in an
 * infinite loop. A module-level guard survives the remount, so confirmation runs once
 * per workspace per session.
 */
const confirmedWorkspaceIds = new Set<string>()

/**
 * Page to be redirected to by chargebee after subscribing.
 * Shows status indication based on "state" url param sent by chargebee in redirect url.
 * 
 * @example <SubscriptionStatusPage />
*/
export const SubscriptionStatusPage: FC = () => {

  const statusColorMap = new Map<Statuses, Color>([
    [Statuses.SUCCESS, Color.SECONDARY_GREEN_DARK],
    [Statuses.PROCESSING, Color.SECONDARY_BLUE_DARK],
    [Statuses.CANCELLED, Color.SECONDARY_ORANGE_DARK],
    [Statuses.ERROR, Color.SECONDARY_RED_DARK],
  ])

  const statusIconMap = new Map<Statuses, IconType>([
    [Statuses.SUCCESS, IconType.CHECK],
    [Statuses.PROCESSING, IconType.LOADING],
    [Statuses.CANCELLED, IconType.DANGER],
    [Statuses.ERROR, IconType.DANGER],
  ])

  const { t } = useTranslation(['subscription_status_page'])
  const [params] = useSearchParams()
  const { currentUserWorkspace } = useUserData()
  const confirmSubscription = useConfirmSubscription()

  const subscriptionStatus = useMemo(() => {
    const rawStatus = params.get('state')
    if (!rawStatus) return Statuses.ERROR

    const status = rawStatus.toUpperCase()
    if (!(status in ChargebeeHostedPageStatus)) return Statuses.ERROR

    switch (status as ChargebeeHostedPageStatus) {
      case ChargebeeHostedPageStatus.SUCCEEDED:
        return Statuses.SUCCESS

      case ChargebeeHostedPageStatus.CANCELLED:
        return Statuses.CANCELLED

      default:
        return Statuses.PROCESSING
    }
  }, [params])

  useEffect(() => {
    if (subscriptionStatus !== Statuses.SUCCESS) return

    const workspaceId = currentUserWorkspace?.id
    if (!workspaceId) return
    if (confirmedWorkspaceIds.has(workspaceId)) return

    confirmedWorkspaceIds.add(workspaceId)
    confirmSubscription.mutate({ workspaceId }, {
      onSuccess: () => {
        // Refresh the USER_ME query so the newly activated paid plan propagates to
        // currentUserWorkspace. The Plan page derives its plan from USER_ME (clientData.workspaces),
        // not from the USER_WORKSPACE query the mutation invalidates — without this it keeps
        // rendering the "choose a plan" options after a successful hosted-page checkout.
        queryClient.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })
      },
      onError: (error) => {
        // Allow a retry on a later mount — confirmation never refreshed USER_ME here,
        // so there is no remount loop to guard against.
        confirmedWorkspaceIds.delete(workspaceId)
        logger.error('Failed to confirm subscription after Chargebee checkout', error, {
          context: { component: 'SubscriptionStatusPage', action: 'confirmSubscription', metadata: { workspaceId } },
        })
      },
    })
  }, [confirmSubscription, currentUserWorkspace?.id, subscriptionStatus])

  return (
    <div className={`page ${styles.paymentStatus} ${!!subscriptionStatus ? styles[subscriptionStatus] : ''}`}>
      <div className="page-content">
        {!!subscriptionStatus &&
          <div className={styles.statusBox}>

            <Icon
              className={`${styles.statusIcon} ${styles[subscriptionStatus]}`}
              color={statusColorMap.get(subscriptionStatus) || Color.SECONDARY_RED_DARK}
              icon={statusIconMap.get(subscriptionStatus) || IconType.CROSS}
            />

            <h2 className={styles.title}>
              {t(`subscription_status_page:${subscriptionStatus}:title`)}
            </h2>

            <p className={styles.text}>
              <Trans
                t={t}
                i18nKey={`subscription_status_page:${subscriptionStatus}:text`}
              >
                <span></span>
              </Trans>
            </p>

            <Link to={Path.INDEX}>
              <Button className={styles.cta}>
                {t('go_to_dashboard')}
              </Button>
            </Link>

          </div>
        }
      </div>
    </div>
  )
}
